/**
 * Axios API Client
 *
 * Configured axios instance with interceptors for:
 * - Automatic response data extraction from ApiResponse format
 * - Request interceptor for auth tokens (placeholder for Cognito)
 * - Error handling
 */

import axios, {
	type AxiosError,
	type AxiosInstance,
	type InternalAxiosRequestConfig,
} from "axios";

// Extend InternalAxiosRequestConfig to carry retry flag
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean;
}

import { env } from "../../config/env";

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
		public data?: unknown,
	) {
		super(message);
		this.name = "ApiError";
	}
}

export interface ApiResponse<T> {
	success: boolean;
	data: T;
	message?: string;
}

/**
 * Create axios instance with base configuration.
 * Uses credentials: 'include' for HttpOnly cookie-based auth — tokens never in browser.
 */
const axiosInstance: AxiosInstance = axios.create({
	baseURL: env.API_URL,
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 30000, // 30 seconds
	withCredentials: true,
});

/**
 * Request interceptor — no token needed; session cookie is sent automatically.
 */
axiosInstance.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => config,
	(error) => Promise.reject(error),
);

/**
 * Response interceptor
 * Extracts data from ApiResponse format: { success: true, data: {...} }
 * Also handles PaginatedResponse format: { data: [...], pagination: {...} }
 */
axiosInstance.interceptors.response.use(
	(response) => {
		const responseData = response.data;

		// Handle PaginatedResponse format first: { data: [...], pagination: {...} }
		// (doesn't have "success" field, so check before ApiResponse)
		if (
			responseData &&
			typeof responseData === "object" &&
			"data" in responseData &&
			"pagination" in responseData &&
			!("success" in responseData)
		) {
			return responseData;
		}

		// Handle ApiResponse format: { success: true, data: {...} }
		if (
			responseData &&
			typeof responseData === "object" &&
			"success" in responseData &&
			"data" in responseData
		) {
			return responseData.data;
		}

		// Return as-is if not in expected format (backward compatibility)
		return responseData;
	},
	async (error: AxiosError) => {
		// Handle error responses
		if (error.response) {
			const status = error.response.status;
			const originalRequest = error.config as
				| RetryableRequestConfig
				| undefined;

			// Handle 401 Unauthorized — session expired or not logged in
			if (status === 401) {
				const errorData = error.response.data as
					| { error?: string; errorCode?: string }
					| undefined;
				const message =
					errorData?.error ||
					"Session expired or not authenticated. Please log in.";
				throw new ApiError(401, message, errorData);
			}

			// Handle 429 Too Many Requests - retry once after 2s backoff
			if (status === 429 && originalRequest && !originalRequest._retry) {
				originalRequest._retry = true;
				await new Promise((resolve) => setTimeout(resolve, 2000));
				return axiosInstance(originalRequest);
			}

			const errorData = error.response.data as
				| { error?: string; message?: string }
				| undefined;
			let message =
				errorData?.error ||
				errorData?.message ||
				`API Error: ${status} ${error.response.statusText}`;
			if (status === 429) {
				message =
					errorData?.message ||
					"Too many requests. Please wait a moment and try again.";
			}

			throw new ApiError(status, message, errorData);
		}

		// Handle network errors
		if (error.request) {
			throw new ApiError(
				0,
				"Network error: Unable to reach server",
				error.request,
			);
		}

		// Handle other errors
		throw new ApiError(
			500,
			error.message || "An unexpected error occurred",
			error,
		);
	},
);

export default axiosInstance;
